import { createApi } from "@reduxjs/toolkit/query/react";
import { parse as _parse } from "date-fns";

import { endOfDay, fromBackendDate, startOfDay, toBackendDate, toDatetime } from "@tsp-core/datetime/utils";
import { baseQuery } from "@tsp-core/store/utils";

import { CurrencyId } from "@tsp-domains/money/enums";
import { StorableMoney } from "@tsp-domains/money/types";
import { transformFromStorableMoney } from "@tsp-domains/money/utils";

import { InvoiceUuid, InvoicesListing, InvoicesListingFilterValues, SupplierInvoiceNumberRecord } from "../types";

type LoadInvoicesRequestParams = {
  search: string;
  periodFrom: number | null;
  periodTo: number | null;
  limit: number;
  offset: number;
};

type LoadInvoicesResponseData = {
  invoices: Array<{
    uuid: InvoiceUuid;
    amount: string;
    periodTs: number;
    currency: CurrencyId;
    description: string;
    approvalDate: string;
    documentNumber: string;
  }>;
  totalCount: number;
  totalSumStorable: StorableMoney;
};

type GetSupplierInvoiceNumbersRequestParams = {
  periodFrom: number | null;
  periodTo: number | null;
};

type GetSupplierInvoiceNumbersResponseData = Array<{
  documentNumber: string;
  periodTs: number;
}>;

export const invoicesApi = createApi({
  reducerPath: "@invoices/api",
  baseQuery,
  refetchOnFocus: false,
  refetchOnReconnect: false,
  refetchOnMountOrArgChange: false,
  tagTypes: ["InvoicesRecordsListing"],
  endpoints: (builder) => ({
    getSupplierInvoiceNumbers: builder.query<Array<SupplierInvoiceNumberRecord>, InvoicesListingFilterValues["period"]>(
      {
        query: (period) => {
          const params: GetSupplierInvoiceNumbersRequestParams = {
            periodFrom: toBackendDate(startOfDay(period[0])),
            periodTo: toBackendDate(endOfDay(period[1])),
          };

          return {
            url: "/payout/getSupplierInvoiceNumbers",
            method: "GET",
            params,
          };
        },
        transformResponse: (data: GetSupplierInvoiceNumbersResponseData): Array<SupplierInvoiceNumberRecord> => {
          return data.map((record) => {
            return {
              documentNumber: record.documentNumber,
              periodTs: fromBackendDate(record.periodTs),
            };
          });
        },
      },
    ),
    loadInvoicesRecordsListing: builder.query<InvoicesListing, InvoicesListingFilterValues>({
      query: (values) => {
        const body: LoadInvoicesRequestParams = {
          search: values.search,
          periodFrom: toBackendDate(startOfDay(values.period[0])),
          periodTo: toBackendDate(endOfDay(values.period[1])),
          limit: values.limit,
          offset: (values.page - 1) * values.limit,
        };

        return {
          url: "/payout/getSupplierInvoices",
          method: "POST",
          body,
        };
      },
      transformResponse: (data: LoadInvoicesResponseData, _, arg): InvoicesListing => {
        return {
          records: data.invoices.map((invoice) => {
            const approvalDateInLocalTimezone = _parse(invoice.approvalDate, "dd.MM.yyyy HH:mm", new Date());
            const approvalDate = new Date(
              Date.UTC(
                approvalDateInLocalTimezone.getFullYear(),
                approvalDateInLocalTimezone.getMonth(),
                approvalDateInLocalTimezone.getDate(),
                approvalDateInLocalTimezone.getHours(),
                approvalDateInLocalTimezone.getMinutes(),
              ),
            );

            return {
              uuid: invoice.uuid,
              approvedDate: toDatetime(approvalDate),
              document: invoice.documentNumber,
              periodDate: fromBackendDate(invoice.periodTs),
              amount: invoice.amount,
              description: invoice.description || "",
              currencyId: invoice.currency,
            };
          }),
          pagination: {
            limit: arg.limit,
            page: arg.page,
            total: data.totalCount,
          },
          meta: {
            sum: transformFromStorableMoney(data.totalSumStorable),
          },
        };
      },
      providesTags: ["InvoicesRecordsListing"],
    }),
    downloadInvoicePdf: builder.mutation<string, InvoiceUuid>({
      query: (uuid) => {
        return {
          url: `/get-invoice-pdf?payoutUuid=${uuid}`,
          method: "GET",
          responseHandler: "text",
        };
      },
    }),
  }),
});
